/* eslint-disable no-param-reassign */
import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import { productPropTypes } from 'helpers/propTypes';
import { formatCurrencyARS } from 'helpers/currency';
import { itemQuantitySelector } from 'redux/selectors/cart.selector';
import { openProductModal } from 'redux/actions/modal.actions';
import { updateCart } from 'redux/actions/cart.actions';
import { useHistory } from 'react-router-dom';

import Item from 'components/commons/Item';
import ItemTextil from 'components/commons/Item/ItemTextil';
import ItemD5 from 'components/commons/Item/ItemD5';
import ItemD7 from 'components/commons/Item/ItemD7';
import { getStoreDesign } from 'constants/storeDesigns';
import { getCustomDesignSettings, isCustomDesign } from 'constants/customDesign';

const ItemContainer = (props) => {
  const {
    cartItem, item, openProductModalAction, updateCartAction, user, onlyUsers, client, cantMedidasMax, 
    cuotasMP, showMPButton, paymentMethods, autoDiscounts,
  } = props;
  const history = useHistory();

  const quantity = cartItem ? cartItem.quantity : 0;
  const colors = item?.stock_product_descriptions?.filter(
    ({ stock_descriptions }) => stock_descriptions?.stock_category_id === 1,
  );
  const talles = item?.stock_product_descriptions?.filter(
    ({ stock_descriptions }) => stock_descriptions?.stock_category_id === 2,
  );

  const combinations = colors?.reduce((a, b) => {
    a[b.stock_description_id] = a[b.stock_description_id] || [];
    const combination = item?.stock_product_descriptions
      ?.filter(({ stock_descriptions }) => stock_descriptions?.stock_category_id !== 1)
      ?.find(
        (stock_product_description) => stock_product_description?.combination === b.combination,
      );
    if (combination) a[b.stock_description_id].push(combination);
    return a;
  }, Object.create(null));

  const combinationsTalle = talles?.reduce((a, b) => {
    a[b.stock_description_id] = a[b.stock_description_id] || [];
    const combinationTalle = item?.stock_product_descriptions
      ?.filter(({ stock_descriptions }) => stock_descriptions?.stock_category_id !== 2)
      ?.find(
        (stock_product_description) => stock_product_description?.combination === b.combination,
      );
    if (combinationTalle) a[b.stock_description_id].push(combinationTalle);
    return a;
  }, Object.create(null));

  const onQuantityChange = (value) => {
    if (value >= 0 && value <= item.stock) {
      updateCartAction(item.id, value, null, {
        id: 0, bool: false, quantity: 0,
      }, null);
    }
  };

  const getValidPriceRange = () => {
    const validPrices = talles.filter((talle) => talle.price !== null && talle.price > 0).map((talle) => talle.price);
    const distinctPrices = Array.from(new Set(validPrices));
    if (distinctPrices.length === 1) {
      return formatCurrencyARS(validPrices[0]);
    }
    const minPrice = Math.min(...distinctPrices);
    const maxPrice = Math.max(...distinctPrices);
    return `${formatCurrencyARS(minPrice)} - ${formatCurrencyARS(maxPrice)}`;
  };

  const slug = item.code || item.id;
  const productHref = `/producto/${slug}`;

  const redireccionToProduct = () => {
    history.push(productHref);
  };

  // "Single" (modal) - used by the Comprar CTA button across designs.
  const openSingleModal = () => {
    openProductModalAction(item);
  };

  const getInstallmentsSummary = () => {
    if (cuotasMP?.error === 'access_token_missing' || showMPButton === false) return null;
    if (!Array.isArray(cuotasMP)) return null;

    let maxZeroInterest = 0;
    let hasInstallments = false; 

    for (const brandData of cuotasMP) {
        if (brandData.issuers && Array.isArray(brandData.issuers)) {
            for (const issuer of brandData.issuers) {
                if (issuer.rates && Array.isArray(issuer.rates)) {
                    for (const rateOption of issuer.rates) {
                        const n = rateOption.installments;
                        const r = Number(rateOption.rate);

                        if (n > 1) hasInstallments = true;

                        if (r === 0 && n > maxZeroInterest) {
                            maxZeroInterest = n;
                        }
                    }
                }
            }
        }
    }

    return { maxZeroInterest, hasInstallments };
  };

  const renderInstallmentsBadge = () => {
    const summary = getInstallmentsSummary();
    if (!summary) return null;

    const { maxZeroInterest, hasInstallments } = summary;
    if (maxZeroInterest >= 2) {
      return (
        <span className="badge-cuotas no-interest" title="Cuotas sin interés disponibles con tarjetas seleccionadas">
          <span className="cc-ico" aria-hidden>💳</span>
          {`Hasta ${maxZeroInterest} cuotas sin interés disponibles con tarjetas seleccionadas`}
        </span>
      );
    }

    if (hasInstallments) {
        return (
        <span className="badge-cuotas with-interest" title="Pagá en cuotas">
            <span className="cc-ico" aria-hidden>💳</span>
            Con cuotas
        </span>
        );
    }
    return null;
  };

  const storeDesign = getStoreDesign(client?.design);
  if (isCustomDesign(client)) {
    const customSettings = getCustomDesignSettings(client);
    const Component = customSettings.cardStyle === 'compact' ? ItemD7 : ItemD5;
    return (
      <Component
        item={item}
        onItemClick={() => openProductModalAction(item)}
        onBuyClick={openSingleModal}
        onQuantityChange={onQuantityChange}
        quantity={quantity}
        talles={talles}
        colors={colors}
        combinations={combinations}
        combinationsTalle={combinationsTalle}
        show={onlyUsers === false || (onlyUsers === true && !!user)}
        client={client}
        getValidPriceRange={getValidPriceRange}
        cantMedidasMax={cantMedidasMax}
        renderInstallmentsBadge={renderInstallmentsBadge}
        paymentMethods={paymentMethods}
        autoDiscounts={autoDiscounts}
      />
    );
  }

  if (storeDesign) {
    const Component = storeDesign.productCard === 'compact' ? ItemD7 : ItemD5;
    return (
      <Component
        item={item}
        onItemClick={() => openProductModalAction(item)}
        onBuyClick={openSingleModal}
        onQuantityChange={onQuantityChange}
        quantity={quantity}
        colors={colors}
        talles={talles}
        combinations={combinations}
        combinationsTalle={combinationsTalle}
        getValidPriceRange={getValidPriceRange}
        show={onlyUsers == false || (onlyUsers == true && !!user)}
        client={client}
        renderInstallmentsBadge={renderInstallmentsBadge}
        paymentMethods={paymentMethods}
        autoDiscounts={autoDiscounts}
      />
    );
  }

  switch (client?.design) {
    case 3:
      if (item?.usoMetros) {
        return (
          <ItemTextil
            item={item}
            onItemClick={() => redireccionToProduct()}
            onBuyClick={openSingleModal}
            onQuantityChange={onQuantityChange}
            quantity={quantity}
            colors={colors}
            talles={talles}
            combinations={combinations}
            combinationsTalle={combinationsTalle}
            getValidPriceRange={getValidPriceRange}
            show={onlyUsers == false || (onlyUsers == true && !!user)}
            client={client}
            cantMedidasMax={cantMedidasMax}
            renderInstallmentsBadge={renderInstallmentsBadge}
            paymentMethods={paymentMethods}
            autoDiscounts={autoDiscounts}
          />
        );
      } else {
        return (
          <Item
            item={item}
            onItemClick={() => redireccionToProduct()}
            onBuyClick={openSingleModal}
            onQuantityChange={onQuantityChange}
            quantity={quantity}
            colors={colors}
            talles={talles}
            combinations={combinations}
            combinationsTalle={combinationsTalle}
            getValidPriceRange={getValidPriceRange}
            show={onlyUsers == false || (onlyUsers == true && !!user)}
            client={client}
            isTextil={true}
            cantMedidasMax={cantMedidasMax}
            renderInstallmentsBadge={renderInstallmentsBadge}
            paymentMethods={paymentMethods}
            autoDiscounts={autoDiscounts}

          />
        );
      }
    case 5:
      return (
        <ItemD5
          item={item}
          onItemClick={() => openProductModalAction(item)}
          onBuyClick={openSingleModal}
          onQuantityChange={onQuantityChange}
          quantity={quantity}
          colors={colors}
          talles={talles}
          combinations={combinations}
          combinationsTalle={combinationsTalle}
          getValidPriceRange={getValidPriceRange}
          show={onlyUsers == false || (onlyUsers == true && !!user)}
          client={client}
          renderInstallmentsBadge={renderInstallmentsBadge}
          paymentMethods={paymentMethods}
          autoDiscounts={autoDiscounts}
        />
      );
    case 6:
      return (
        <ItemD5
          item={item}
          onItemClick={() => openProductModalAction(item)}
          onBuyClick={openSingleModal}
          onQuantityChange={onQuantityChange}
          quantity={quantity}
          colors={colors}
          talles={talles}
          combinations={combinations}
          combinationsTalle={combinationsTalle}
          getValidPriceRange={getValidPriceRange}
          show={onlyUsers == false || (onlyUsers == true && !!user)}
          client={client}
          renderInstallmentsBadge={renderInstallmentsBadge}
          paymentMethods={paymentMethods}
          autoDiscounts={autoDiscounts}
        />
      );
    case 7:
      return (
        <ItemD7
          item={item}
          onItemClick={() => openProductModalAction(item)}
          onBuyClick={openSingleModal}
          onQuantityChange={onQuantityChange}
          quantity={quantity}
          colors={colors}
          talles={talles}
          combinations={combinations}
          combinationsTalle={combinationsTalle}
          getValidPriceRange={getValidPriceRange}
          show={onlyUsers == false || (onlyUsers == true && !!user)}
          client={client}
          renderInstallmentsBadge={renderInstallmentsBadge}
          paymentMethods={paymentMethods}
          autoDiscounts={autoDiscounts}
        />
      );
    default:
      return (
        <Item
          item={item}
          onItemClick={() => openProductModalAction(item)}
          onBuyClick={openSingleModal}
          onQuantityChange={onQuantityChange}
          quantity={quantity}
          colors={colors}
          talles={talles}
          combinations={combinations}
          combinationsTalle={combinationsTalle}
          getValidPriceRange={getValidPriceRange}
          show={onlyUsers == false || (onlyUsers == true && !!user)}
          client={client}
          renderInstallmentsBadge={renderInstallmentsBadge}
          paymentMethods={paymentMethods}
          autoDiscounts={autoDiscounts}
        />
      );
  }
};

const mapStateToProps = (state, props) => ({
  cartItem: itemQuantitySelector(state, props),
  user: state.user.data,
  onlyUsers: state.app.onlyUsers,
  client: state.app.client,
  cuotasMP: state.app.cuotasMP,
  showMPButton: state.app.showMPButton,
  paymentMethods: state.app.paymentMethods,
  autoDiscounts: state.app.discount,
});

const mapDispatchToProps = (dispatch) => ({
  openProductModalAction: bindActionCreators(openProductModal, dispatch),
  updateCartAction: bindActionCreators(updateCart, dispatch),
});

ItemContainer.defaultProps = {
  cartItem: null,
};

ItemContainer.propTypes = {
  cartItem: PropTypes.shape({
    itemId: PropTypes.number.isRequired,
    quantity: PropTypes.number.isRequired,
  }),
  item: productPropTypes.isRequired,
  openProductModalAction: PropTypes.func.isRequired,
  updateCartAction: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(ItemContainer);
